import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Dialog, Grid, LinearProgress } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';

import { Delete as DeleteIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

import _ from 'lodash';

import { GridSpacing } from '@mui/material/Grid';
import {
  IDispatchProps, IProps, ISearchChip, IState, IStateProps,
} from './interfaces';
import {
  getAllSettingsAction,
  selectVaultPublishSettings,
} from '../../../../state/publishEndpoints';
import {
  IPublishSettings,
  ISettingsBase,
  SettingType,
} from '../../../../api/vault/publishEndpoints';
import {
  ActionContainer,
  DialogActionsOverride,
  DialogContentContainer,
  DialogHeader,
  DialogHeaderTitle,
  ProgressContainer,
  PublishProfilesContainer,
  TextFieldOverride,
  TextHeader,
} from './styles';
import { SelectionModel } from '../../../../cdk/selection';
import { IAppState } from '../../../../state/reducers';
import VaultButton from '../../VaultButton';
import { KeyboardKey } from '../../../../enums';
import { KeyRoleUserVaultRoles, selectIsUserVaultSuperAdmin } from '../../../../state/user/userOrgRole';
import AssetTitle from './AssetTitle/component';
import SelectedAssetsInfo from './SelectedAssetsInfo/component';
import { IPublishAsset, PublishRenditionType } from '../../../../api/vault/publishAssets';
import toast, { superAdminToast } from '../../../../utils/toast';

class PublishProfilesDialog extends PureComponent<IProps> {
  private static readonly publishText = 'Publish';
  private static readonly publishCheckbox = 'checkbox';

  private static readonly publishButtonID = `${PublishProfilesDialog.publishText}.profile`;
  private static readonly cancelButtonID = `${PublishProfilesDialog.publishText}.cancel`;

  private static readonly profileClearFilterButtonID = `${PublishProfilesDialog.publishText}.Button.ClearFilters`;
  private static readonly profileFilterInputID = `${PublishProfilesDialog.publishText}.Input.ProfileFilter`;

  state: Readonly<IState> = {
    profileSearchChips: [],
    profileSearch: '',
    publishProfilesSelection: new SelectionModel<IPublishSettings<ISettingsBase>>(),
    renditionsMap: new Map<string, PublishRenditionType>(),
    unPublishableAssets: [],
  };

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<{}>, snapshot?: any): void {
    const { renditions, selectedAssets } = this.props;
    if (prevProps.renditions !== renditions && renditions) {
      const unPublishableAssets: string[] = [];
      const map = new Map<string, PublishRenditionType>();
      renditions.forEach((availableRenditions, assetId) => {
        if (availableRenditions.length === 0) {
          unPublishableAssets.push(assetId);
        } else {
          let defaultRendition = PublishRenditionType.Full;
          if (!availableRenditions.find((r) => r.type === PublishRenditionType.Full)) {
            defaultRendition = availableRenditions[0].type;
          }
          map.set(assetId, defaultRendition);
        }
      });
      if (unPublishableAssets.length > 0) {
        const unPublishableAssetTitles = selectedAssets.filter((asset) => unPublishableAssets.includes(asset._id)).map((asset) => asset._source.title).join(', ');
        superAdminToast(false, `Unable to publish ${unPublishableAssets.length} asset${unPublishableAssets.length === 1 ? '' : 's'} since there are no avaiable renditions:\n${unPublishableAssetTitles}`, { type: toast.TYPE.ERROR });
      }

      this.setState({
        renditionsMap: map,
        unPublishableAssets,
      });
    }
  }

  closeDialogBox = () => {
    const { onClose } = this.props;
    this.state.publishProfilesSelection.deselectAll();
    this.setState({
      profileSearch: '',
      profileSearchChips: [],
      renditionsMap: new Map<string, PublishRenditionType>(),
    }, () => {
      onClose();
    });
  };

  handleSelectRendition = (assetId: string, rendition: PublishRenditionType) => {
    const map = this.state.renditionsMap;
    map.set(assetId, rendition);
    this.setState((previousState) => ({
      ...previousState,
      renditionsMap: new Map(map),
    }));
  };

  handlePublish = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { onPublish } = this.props;
    const { renditionsMap } = this.state;
    const assets: IPublishAsset[] = [];
    renditionsMap.forEach((rendition, assetId) => {
      assets.push({ assetId, renditionType: rendition });
    });
    onPublish(this.state.publishProfilesSelection.getAll(), null, assets);
    this.closeDialogBox();
  };

  handleSelectedPublishSettings = (e: React.ChangeEvent<HTMLButtonElement>, publishSetting: IPublishSettings<ISettingsBase>) => {
    this.state.publishProfilesSelection.toggle(publishSetting);
    this.forceUpdate();
  };

  groupProfiles = () => {
    const { publishSettings } = this.props;

    if (_.isNil(publishSettings)) {
      return new Map<string, IPublishSettings<ISettingsBase>[]>();
    }

    const map = new Map<string, IPublishSettings<ISettingsBase>[]>();

    publishSettings
      .forEach((s) => {
        if (map.has(s.type)) {
          const grouping = map.get(s.type);

          grouping.push(s);
        } else {
          map.set(s.type, [s]);
        }
      });

    return map;
  };

  renderExistingPublishProfiles = () => {
    const { publishSettings } = this.props;
    const { profileSearchChips } = this.state;

    if (_.isNil(publishSettings)) {
      return null;
    }

    const groups = this.groupProfiles();

    const renderItems: JSX.Element[] = [];

    groups.forEach((profiles, k) => {
      let header = null;

      if (k === SettingType.LAN) {
        header = <TextHeader>LAN/SAN</TextHeader>;
      } else if (k === SettingType.Yahoo) {
        header = <TextHeader>Yahoo</TextHeader>;
      }

      const filtered = profiles
        .filter((p) => {
          if (profileSearchChips && profileSearchChips.length > 0) {
            const lower = p.settings.profileName.toLowerCase();
            return profileSearchChips.find((s) => lower.indexOf(s.value.toLowerCase()) > -1);
          }
          return true;
        });

      if (filtered.length > 0) {
        renderItems.push(
          (
            <Grid item key={Math.random()}>
              <Grid container direction="column">
                <Grid item>
                  {header}
                </Grid>
                {
                  filtered.map((profile) => {
                    const checkBoxId = `${profile.settings.profileName}_${PublishProfilesDialog.publishCheckbox}`;
                    const checkBoxValue = profile.settings.profileName;

                    return (
                      <Grid key={profile._id} item>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              color="secondary"
                              id={checkBoxId}
                              checked={this.state.publishProfilesSelection.isSelected(profile)}
                              onChange={(e) => this.handleSelectedPublishSettings(e as any, profile)}
                              value={checkBoxValue}
                            />
                          )}
                          label={checkBoxValue}
                        />
                      </Grid>

                    );
                  })
                }
              </Grid>
            </Grid>
          ),
        );
      }
    });

    return (
      <Grid container direction="row" spacing={groups.size as GridSpacing}>
        {
          renderItems
        }
      </Grid>
    );
  };

  componentDidMount(): void {
    this.props.getAllPublishSettingsConnect();
  }

  handleSearchKeyPress(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === KeyboardKey.Enter) {
      const { profileSearch } = this.state;

      if (profileSearch) {
        const { profileSearchChips } = this.state;

        profileSearchChips.push({ id: Math.random(), value: profileSearch });

        this.setState({
          profileSearch: '',
          profileSearchChips,
        });
      }
    }
  }

  handleChipDelete(chipToDelete: ISearchChip) {
    const { profileSearchChips } = this.state;

    this.setState({
      profileSearchChips: profileSearchChips.filter((chip) => chip.id !== chipToDelete.id),
    });
  }

  isPublishButtonDisabled = () => {
    const {
      selectedAssets,
      publishingMetadata,
      isLoading,
    } = this.props;
    const { unPublishableAssets } = this.state;
    if (isLoading) return true;
    if (selectedAssets && unPublishableAssets.length === selectedAssets.length) return true;
    if (!selectedAssets) return (publishingMetadata && !publishingMetadata.title) || !this.isProfilesSelected();
    return !this.isProfilesSelected();
  };

  isProfilesSelected = () => {
    const { publishProfilesSelection } = this.state;

    return publishProfilesSelection.count() !== 0;
  };

  render() {
    const {
      isDialogOpen,
      selectedAssets,
      renditions,
      isLoading,
      onDialogEntered,
      onDialogExited,
    } = this.props;
    const { profileSearch, profileSearchChips } = this.state;

    const filterElements: JSX.Element[] = [];

    if (profileSearchChips && profileSearchChips.length > 0) {
      filterElements.push(
        <IconButton
          id={PublishProfilesDialog.profileClearFilterButtonID}
          key={Math.random()}
          aria-label="clear filters"
          onClick={(e) => this.setState({ profileSearchChips: [] })}
          size="large"
        >
          <DeleteIcon />
        </IconButton>,
      );

      profileSearchChips.forEach((c) => filterElements.push(
        <Chip
          key={c.id}
          label={c.value}
          onDelete={(e) => this.handleChipDelete(c)}
        />,
      ));
    }

    return (
      <Dialog
        open={isDialogOpen}
        fullWidth
        maxWidth="md"
        data-auto="VaultPublishDialog"
        TransitionProps={{
          onEntered: onDialogEntered,
          onExited: onDialogExited,
        }}
      >
        <DialogHeader>
          <DialogHeaderTitle>
            {PublishProfilesDialog.publishText}
          </DialogHeaderTitle>

          <TextFieldOverride
            id={PublishProfilesDialog.profileFilterInputID}
            multiline={false}
            margin="dense"
            label="Add Filter"
            variant="outlined"
            value={profileSearch}
            onKeyPress={(e) => this.handleSearchKeyPress(e)}
            onChange={(e) => this.setState({ profileSearch: e.target.value })}
          />
          {filterElements}
        </DialogHeader>
        <ProgressContainer>
          {isLoading && <LinearProgress />}
        </ProgressContainer>
        <DialogContentContainer>
          <PublishProfilesContainer>
            {this.renderExistingPublishProfiles()}
          </PublishProfilesContainer>
          {selectedAssets === null
            ? <AssetTitle />
            : (
              <SelectedAssetsInfo
                unPublishableAssets={this.state.unPublishableAssets}
                handleSelectRendition={this.handleSelectRendition}
                renditionsMap={this.state.renditionsMap}
                renditions={renditions}
                selectedAssets={selectedAssets}
              />
            )}
        </DialogContentContainer>
        <DialogActionsOverride disableSpacing>
          <ActionContainer>
            <VaultButton
              id={PublishProfilesDialog.cancelButtonID}
              variant="plainOnPaper"
              onClick={this.closeDialogBox}
              data-auto="VaultCancelBtn"
            >
              Cancel
            </VaultButton>
            <VaultButton
              id={PublishProfilesDialog.publishButtonID}
              disabled={this.isPublishButtonDisabled()}
              variant="primaryOnWhite"
              onClick={this.handlePublish}
              data-auto="VaultPublishBtn"
            >
              {PublishProfilesDialog.publishText}
            </VaultButton>
          </ActionContainer>
        </DialogActionsOverride>
      </Dialog>
    );
  }
}
const mapStateToProps = (state: IAppState): IStateProps => ({
  publishSettings: selectVaultPublishSettings(state),
  userOrgRoles: state.user?.userOrgRole?.loginAs?.[KeyRoleUserVaultRoles] ?? [],
  securityProfiles: state.securityProfiles?.idValueMapping ?? [],
  publishingMetadata: state.publishSelectedAssetsToEndPoints?.socialPublishing?.metadata,
  renditions: state.selectedAssetRenditions?.renditions,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  isLoading: state.selectedAssetRenditions?.isLoading ?? false,
});

const mapDispatchToProps: IDispatchProps = {
  getAllPublishSettingsConnect: getAllSettingsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PublishProfilesDialog);
